import { useCallback } from 'react';

/**
 * Хук для работы с Telegram Mini App SDK
 * Обёртка над window.Telegram.WebApp
 */
export function useTelegram() {
  const tgWebApp = (window as any).Telegram?.WebApp;

  /** Данные текущего пользователя Telegram */
  const tgUser = tgWebApp?.initDataUnsafe?.user || null;

  /** Сырые initData для авторизации */
  const initDataRaw = tgWebApp?.initData || '';

  /** Цветовая схема (light/dark) */
  const colorScheme = tgWebApp?.colorScheme || 'light';

  /** Высота viewport */
  const viewportHeight = tgWebApp?.viewportStableHeight || window.innerHeight;

  /**
   * Показать нативный диалог подтверждения
   */
  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (tgWebApp?.showConfirm) {
        tgWebApp.showConfirm(message, (confirmed: boolean) => {
          resolve(confirmed);
        });
      } else {
        resolve(window.confirm(message));
      }
    });
  }, [tgWebApp]);

  /**
   * Показать нативный алерт
   */
  const showAlert = useCallback((message: string): Promise<void> => {
    return new Promise((resolve) => {
      if (tgWebApp?.showAlert) {
        tgWebApp.showAlert(message, () => resolve());
      } else {
        window.alert(message);
        resolve();
      }
    });
  }, [tgWebApp]);

  /**
   * Haptic feedback (вибрация)
   */
  const hapticFeedback = useCallback((type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning') => {
    try {
      if (type === 'success' || type === 'error' || type === 'warning') {
        tgWebApp?.HapticFeedback?.notificationOccurred(type);
      } else {
        tgWebApp?.HapticFeedback?.impactOccurred(type);
      }
    } catch {
      // Haptic не поддерживается
    }
  }, [tgWebApp]);

  /**
   * Управление кнопкой "Назад"
   */
  const setBackButtonVisible = useCallback((visible: boolean, callback?: () => void) => {
    try {
      if (visible) {
        tgWebApp?.BackButton?.show();
        if (callback) {
          tgWebApp?.BackButton?.onClick(callback);
        }
      } else {
        tgWebApp?.BackButton?.hide();
        tgWebApp?.BackButton?.offClick();
      }
    } catch {
      // Вне Telegram
    }
  }, [tgWebApp]);

  /**
   * Закрыть Mini App
   */
  const closeMiniApp = useCallback(() => {
    tgWebApp?.close();
  }, [tgWebApp]);

  /**
   * Получить startapp параметр (deep link)
   */
  const getStartParam = useCallback((): string | null => {
    return tgWebApp?.initDataUnsafe?.start_param || null;
  }, [tgWebApp]);

  return {
    tgUser,
    initDataRaw,
    colorScheme,
    viewportHeight,
    showConfirm,
    showAlert,
    hapticFeedback,
    setBackButtonVisible,
    closeMiniApp,
    getStartParam,
    isInTelegram: !!tgWebApp,
  };
}
